import React from 'react';
import { Helmet } from 'react-helmet';

const PRESS_KIT_STYLES = `
  @page { size: A4; margin: 18mm 16mm; }

  .press-kit-root {
    --pk-ink: #0e1a1f;
    --pk-muted: #5b6b73;
    --pk-rule: #d7dde0;
    --pk-accent: #0b3d44;
    --pk-bg: #ffffff;
    --pk-sans: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }

  .press-kit-root, .press-kit-root * { box-sizing: border-box; }

  body.press-kit-body { margin: 0; padding: 0; background: var(--pk-bg); }

  .press-kit-root {
    max-width: 720px;
    margin: 0 auto;
    padding: 56px 32px;
    background: var(--pk-bg);
    color: var(--pk-ink);
    font-family: var(--pk-sans);
    font-size: 10.5pt;
    line-height: 1.5;
  }

  .press-kit-root h1, .press-kit-root h2, .press-kit-root h3 {
    font-family: var(--pk-sans); font-weight: 700; color: var(--pk-ink);
  }
  .press-kit-root h1 { font-size: 22pt; line-height: 1.15; letter-spacing: -0.01em; margin: 0 0 8px; }
  .press-kit-root h2 { font-size: 13pt; margin: 26px 0 8px; letter-spacing: -0.005em; }
  .press-kit-root h3 { font-size: 10.5pt; margin: 14px 0 4px; text-transform: uppercase; letter-spacing: 0.08em; color: var(--pk-accent); }
  .press-kit-root p { margin: 0 0 8px; }

  .press-kit-root .pk-label {
    display: inline-block;
    font-size: 8.5pt;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: var(--pk-muted);
    margin-bottom: 4px;
  }
  .press-kit-root .pk-header {
    border-bottom: 1px solid var(--pk-rule);
    padding-bottom: 18px;
    margin-bottom: 20px;
  }
  .press-kit-root .pk-header__meta { font-size: 9pt; color: var(--pk-muted); margin-top: 6px; }
  .press-kit-root .pk-section { page-break-inside: avoid; margin-bottom: 18px; }
  .press-kit-root .pk-lang-block {
    border-left: 2px solid var(--pk-accent);
    padding-left: 14px;
    margin: 10px 0 18px;
  }
  .press-kit-root .pk-lang-block__lang {
    font-size: 8.5pt;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: var(--pk-accent);
    font-weight: 700;
    margin-bottom: 8px;
  }
  .press-kit-root .pk-lang-block p { margin-bottom: 10px; }
  .press-kit-root .pk-lang-block p:last-child { margin-bottom: 0; }
  .press-kit-root .pk-key-facts { width: 100%; border-collapse: collapse; margin: 6px 0; }
  .press-kit-root .pk-key-facts td {
    padding: 6px 12px 6px 0;
    vertical-align: top;
    border-bottom: 1px solid var(--pk-rule);
    font-size: 10pt;
  }
  .press-kit-root .pk-key-facts td:first-child {
    width: 32%; font-weight: 600; color: var(--pk-ink); white-space: nowrap;
  }
  .press-kit-root .pk-key-facts tr:last-child td { border-bottom: 0; }
  .press-kit-root .pk-founders { display: grid; grid-template-columns: 1fr; gap: 10px; margin: 6px 0; }
  .press-kit-root .pk-founder { padding-left: 14px; border-left: 2px solid var(--pk-rule); }
  .press-kit-root .pk-founder__name { font-weight: 700; font-size: 10.5pt; }
  .press-kit-root .pk-founder__role { color: var(--pk-accent); font-size: 9.5pt; margin-bottom: 2px; }
  .press-kit-root .pk-founder__bio { color: var(--pk-ink); font-size: 10pt; }
  .press-kit-root .pk-press { width: 100%; border-collapse: collapse; font-size: 9.5pt; }
  .press-kit-root .pk-press th, .press-kit-root .pk-press td {
    text-align: left; padding: 5px 8px 5px 0; vertical-align: top; border-bottom: 1px solid var(--pk-rule);
  }
  .press-kit-root .pk-press th {
    font-weight: 600; font-size: 8.5pt; text-transform: uppercase;
    letter-spacing: 0.1em; color: var(--pk-muted);
  }
  .press-kit-root .pk-press td:first-child { width: 60px; color: var(--pk-muted); }
  .press-kit-root .pk-press td:nth-child(2) { width: 30%; font-weight: 600; }
  .press-kit-root .pk-press a {
    color: var(--pk-accent); text-decoration: none; border-bottom: 1px solid var(--pk-rule);
  }
  .press-kit-root .pk-contact {
    background: #f4f6f7; padding: 14px 16px; border-radius: 4px; margin-top: 12px;
  }
  .press-kit-root .pk-contact a { color: var(--pk-accent); text-decoration: none; font-weight: 600; }
  .press-kit-root .pk-footer {
    margin-top: 32px; padding-top: 14px; border-top: 1px solid var(--pk-rule);
    font-size: 8.5pt; color: var(--pk-muted);
    display: flex; justify-content: space-between;
  }
  .press-kit-root a { color: var(--pk-accent); }
  @media print {
    .press-kit-root { padding: 0; }
    .press-kit-root a { text-decoration: none; }
    .press-kit-root .pk-press a { border-bottom: 0; }
  }
`;

const PressKit: React.FC = () => {
  React.useEffect(() => {
    document.body.classList.add('press-kit-body');
    return () => {
      document.body.classList.remove('press-kit-body');
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Press kit · Thoughtful Oasis</title>
        <meta
          name="description"
          content="Press kit for Thoughtful Oasis: boilerplate, key facts, founders, press coverage and contact."
        />
        <link rel="canonical" href="https://thoughtful-oasis.com/documents/press-kit" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Press kit · Thoughtful Oasis" />
        <meta
          property="og:description"
          content="Boilerplate, key facts, founder bios, press coverage and contact."
        />
        <meta property="og:url" content="https://thoughtful-oasis.com/documents/press-kit" />
        <style>{PRESS_KIT_STYLES}</style>
      </Helmet>

      <article className="press-kit-root">
        <div className="pk-header">
          <span className="pk-label">Press kit</span>
          <h1>Thoughtful Oasis</h1>
          <p className="pk-header__meta">
            An AI research lab building interpretable AI. Enschede, the Netherlands. thoughtful-oasis.com ·
            hello@thoughtful-oasis.com
          </p>
        </div>

        <section className="pk-section">
          <h2>Boilerplate: English</h2>

          <div className="pk-lang-block">
            <div className="pk-lang-block__lang">One-liner</div>
            <p>
              Thoughtful Oasis is an AI research lab building a new kind of AI: one whose reasoning is built
              from concepts a human can read.
            </p>
          </div>

          <div className="pk-lang-block">
            <div className="pk-lang-block__lang">Three sentences</div>
            <p>
              The current generation of AI reasons inside a hidden numerical space: embeddings with hundreds or
              thousands of dimensions no one can name. Thoughtful Oasis rebuilt that substrate on named axes:
              concepts a domain expert recognises and can defend, with the same operations and comparable
              capability. The lab works from Enschede, the Netherlands, and is running early pilots with the
              regional safety authority VNOG and the Dutch Ministry of Justice and Security.
            </p>
          </div>

          <div className="pk-lang-block">
            <div className="pk-lang-block__lang">Paragraph</div>
            <p>
              Thoughtful Oasis is an AI research lab based in Enschede, the Netherlands, founded by Kaspar
              Rothenfusser, Bekk Blando and Rens Dommerholt. The company builds an interpretable substrate for
              AI: instead of reasoning inside hundreds of unnamed dimensions, the system reasons along named
              axes (concepts like urgency, jurisdiction or evidentiary weight) that a domain expert recognises
              and can defend. The underlying framework is published in three papers, two of which are openly
              available on arXiv; the most recent shows the approach reaches recall@2 = 1.000 on the LIMIT
              retrieval benchmark, against 0.000–0.019 for current state-of-the-art dense retrievers. The first
              applications run in domains where wrong answers are expensive: with VNOG, incident commanders get
              a full scenario analysis within one minute of dispatch, with every recommendation traceable to
              its source manual; with the Ministry of Justice and Security, citizens file freedom-of-information
              requests in plain language and the system answers what is already public before a clerk gets
              involved. Partners include the University of Twente, EIT Digital, AIC4NL, Kickstart AI and
              NovelT.
            </p>
          </div>
        </section>

        <section className="pk-section">
          <h2>Boilerplate: Nederlands</h2>

          <div className="pk-lang-block">
            <div className="pk-lang-block__lang">Oneliner</div>
            <p>
              Thoughtful Oasis is een AI-onderzoekslab dat een ander soort AI bouwt: een AI waarvan elke
              dimensie een concept is dat een mens kan lezen.
            </p>
          </div>

          <div className="pk-lang-block">
            <div className="pk-lang-block__lang">Drie zinnen</div>
            <p>
              De huidige generatie AI denkt in honderden of duizenden getallen waar niemand een naam aan kan
              geven; voor consumenten-apps werkt dat, voor besluiten waar fouten duur zijn niet. Thoughtful
              Oasis bouwt een AI-laag waarvan de assen wél benoemd zijn: concepten die een domeinexpert
              herkent en kan verdedigen, met dezelfde operaties en vergelijkbare capaciteit als de huidige
              modellen. Het lab werkt vanuit Enschede en draait pilots met Veiligheidsregio Noord- en
              Oost-Gelderland (VNOG) en het Ministerie van Justitie en Veiligheid.
            </p>
          </div>

          <div className="pk-lang-block">
            <div className="pk-lang-block__lang">Paragraaf</div>
            <p>
              Thoughtful Oasis is een Nederlands AI-onderzoekslab uit Enschede, opgericht door Kaspar
              Rothenfusser, Bekk Blando en Rens Dommerholt. Het bedrijf bouwt aan een interpreteerbare AI-laag:
              in plaats van te redeneren in honderden ongenoemde dimensies, redeneert het systeem langs
              benoemde assen (concepten als urgentie, jurisdictie of bewijskracht) die een domeinexpert
              herkent en kan verdedigen. De onderliggende theorie staat in drie papers, twee daarvan openbaar
              op arXiv; de meest recente laat zien dat de aanpak op de LIMIT retrieval-benchmark recall@2 =
              1.000 haalt, tegenover 0.000–0.019 voor de huidige state-of-the-art dense retrievers. De eerste
              toepassingen draaien in domeinen waar antwoorden verdedigbaar moeten zijn: bij VNOG krijgen
              bevelvoerders binnen één minuut na uitruk een volledige scenario-analyse met elke aanbeveling
              herleidbaar tot de bron; bij het Ministerie van Justitie en Veiligheid dienen burgers
              Woo-verzoeken in gewone taal in en beantwoordt het systeem wat al openbaar is voordat een
              ambtenaar erbij komt. Samenwerkingen lopen onder meer met Universiteit Twente, EIT Digital,
              AIC4NL, Kickstart AI en NovelT.
            </p>
          </div>
        </section>

        <section className="pk-section">
          <h2>Key facts</h2>
          <table className="pk-key-facts">
            <tbody>
              <tr>
                <td>Legal entity</td>
                <td>Thoughtful Oasis BV</td>
              </tr>
              <tr>
                <td>Headquarters</td>
                <td>Enschede, the Netherlands</td>
              </tr>
              <tr>
                <td>Founders</td>
                <td>
                  Kaspar Rothenfusser (CSO, German), Bekk Blando (CTO, American), Rens Dommerholt (CEO, Dutch)
                </td>
              </tr>
              <tr>
                <td>Focus</td>
                <td>
                  Interpretable AI: named-axis embeddings, vector ontologies, reasoning that traces back to
                  concepts a human can read
                </td>
              </tr>
              <tr>
                <td>Active pilots</td>
                <td>
                  VNOG (Veiligheidsregio Noord- en Oost-Gelderland), Ministerie van Justitie en Veiligheid
                </td>
              </tr>
              <tr>
                <td>Research</td>
                <td>3 papers: 2 on arXiv, 1 preprint on request</td>
              </tr>
              <tr>
                <td>Ecosystem partners</td>
                <td>Universiteit Twente, EIT Digital, AIC4NL, Kickstart AI, NovelT</td>
              </tr>
              <tr>
                <td>Website</td>
                <td>thoughtful-oasis.com</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section className="pk-section">
          <h2>Founders</h2>
          <div className="pk-founders">
            <div className="pk-founder">
              <div className="pk-founder__name">Kaspar Rothenfusser</div>
              <div className="pk-founder__role">Chief Science Officer · German</div>
              <div className="pk-founder__bio">
                Wrote the formal framework behind vector ontologies and led the first empirical validation.
                Sets research direction.
              </div>
            </div>
            <div className="pk-founder">
              <div className="pk-founder__name">Bekk Blando</div>
              <div className="pk-founder__role">Chief Technology Officer · American</div>
              <div className="pk-founder__bio">
                Builds the production layer. Owns architecture, deployment, and the API used by pilot partners.
              </div>
            </div>
            <div className="pk-founder">
              <div className="pk-founder__name">Rens Dommerholt</div>
              <div className="pk-founder__role">Chief Executive Officer · Dutch</div>
              <div className="pk-founder__bio">
                Runs operations: commercial, partnerships, compliance, delivery. Connects the research to the
                customers it fits.
              </div>
            </div>
          </div>
        </section>

        <section className="pk-section">
          <h2>Selected press &amp; coverage</h2>
          <table className="pk-press">
            <thead>
              <tr>
                <th>Year</th>
                <th>Outlet</th>
                <th>Title</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>2026</td>
                <td>Platform AI &amp; Overheid</td>
                <td>
                  <a href="https://platformaioverheid.nl/wp-content/uploads/2026/02/Resultaten_onderzoek_AI_en_Open_Overheid_februari_2026_def.pdf">
                    AI &amp; Open Overheid — research report
                  </a>
                </td>
              </tr>
              <tr>
                <td>2025</td>
                <td>ECP</td>
                <td>
                  <a href="https://ecp.nl/terugblikhackathonterminalwoo/">
                    Terminal WOO hackathon recap
                  </a>
                </td>
              </tr>
              <tr>
                <td>2025</td>
                <td>Vakblad Od (editie 49)</td>
                <td>
                  <a href="https://magazines.od-online.nl/od-editie-49/hackathon-hoe-verbeter-je-het-woo-proces">
                    Hackathon — hoe verbeter je het Woo-proces
                  </a>
                </td>
              </tr>
              <tr>
                <td>2025</td>
                <td>NL Startup Competition</td>
                <td>
                  <a href="https://www.linkedin.com/posts/nl-startup-competition_failing-learning-and-building-rens-domerholt-activity-7424731020505272320-502K">
                    Failing, learning and building — Rens Dommerholt
                  </a>
                </td>
              </tr>
              <tr>
                <td>2025</td>
                <td>Startup Pass-port (podcast)</td>
                <td>
                  <a href="https://open.spotify.com/episode/0yRKaGLu3h6fPFDC4iv8Kl">
                    Failing, Learning and Building — Rens Dommerholt
                  </a>
                </td>
              </tr>
              <tr>
                <td>2025</td>
                <td>EIT AI Community</td>
                <td>
                  <a href="https://ai.eitcommunity.eu/ai-founders-club-thoughtful-oasis">
                    AI Founders Club profile
                  </a>
                </td>
              </tr>
              <tr>
                <td>2025</td>
                <td>Novel-T</td>
                <td>
                  <a href="https://novelt.com/en/cases/244/thoughtful-oasis">
                    Search technology for complex queries
                  </a>
                </td>
              </tr>
              <tr>
                <td>2025</td>
                <td>AIC4NL</td>
                <td>
                  <a href="https://aic4nl.nl/en/deelnemers/thoughtfuloasis/">Participant profile</a>
                </td>
              </tr>
              <tr>
                <td>2025</td>
                <td>Dutch AI Startup Map</td>
                <td>
                  <a href="https://dashboard.dutchstartup.ai/dashboard?s=STU_241">Listing — STU_241</a>
                </td>
              </tr>
            </tbody>
          </table>
          <p style={{ marginTop: 10, fontSize: '9pt', color: 'var(--pk-muted)' }}>
            A live, full press list is maintained at{' '}
            <a href="https://thoughtful-oasis.com/news">thoughtful-oasis.com/news</a>.
          </p>
        </section>

        <section className="pk-section">
          <h2>Press contact</h2>
          <div className="pk-contact">
            <p>
              <strong>General / interview requests:</strong>{' '}
              <a href="mailto:hello@thoughtful-oasis.com">hello@thoughtful-oasis.com</a>
            </p>
            <p>
              <strong>Rens Dommerholt directly:</strong>{' '}
              <a href="mailto:rens@thoughtful-oasis.com">rens@thoughtful-oasis.com</a>
            </p>
            <p style={{ marginTop: 6, color: 'var(--pk-muted)', fontSize: '9.5pt' }}>
              Founder photos and logos are available at{' '}
              <a href="https://thoughtful-oasis.com/documents">thoughtful-oasis.com/documents</a> or on request.
            </p>
          </div>
        </section>

        <div className="pk-footer">
          <span>© 2026 Thoughtful Oasis BV · Enschede, the Netherlands</span>
          <span>Last updated: 2026-05-12</span>
        </div>
      </article>
    </>
  );
};

export default PressKit;
